import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Typography, Skeleton, DataTable } from '@chilecompra/react-kit/components'
import { Select, Spinner, useEffectOnce, usePathParam, useSelect, useViewport } from '@chilecompra/react-kit'
import { CLAIMS_OPTIONS_DICTIONARY, CLAIMS_OPTIONS } from '../../config/settings/constant'
import { PUBLIC_URL, REACT_APP_SEARCH_CLAIM_INFO } from '../../config/settings/environment'
import { exportToExcel } from '../../modules/utils/exports'
import { onGetClaimInformationTableThunk } from './ClaimsReceivedChartSection.actions'
import {
  ArrowDownIcon,
  ArrowForwardIcon,
  Card,
  CardItem,
  ColumnChart,
  GridItem,
  Link,
  NotFoundImg,
  Root,
  Row,
  RowChart,
  TableContainer,
  TableLink,
  WarningImg
} from './ClaimsReceivedChartSection.styles'

/**
 * The Claims section container.
 */
const ClaimsReceivedChartSection = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const {
    size: { isTiny, isUpToMedium }
  } = useViewport()

  const { value: claimValue, onChange: handleChangeOption } = useSelect({
    initialValue: CLAIMS_OPTIONS_DICTIONARY.all,
    changeCallback: claimValue => {
      dispatch(onGetClaimInformationTableThunk(dni, claimValue))
    }
  })
  const { error, loading, hasData, excelData, gridData, loadingSpinner } = useSelector(
    state => state.claimInformationTable
  )

  const totalRows = 10
  const [dataInfo, setdataInfo] = useState([])

  const headerTable = [
    { label: 'ID Reclamo', value: 'claimId' },
    { label: 'Proceso reclamado', value: 'processClaim' },
    { label: 'Tipo de reclamo', value: 'typeOfClaim' },
    { label: 'Fecha de ingreso', value: 'entryDate' },
    { label: 'Reclamante', value: 'complainant' },
    { label: 'Estado reclamo', value: 'complainant' }
  ]
  const claimTableProp = [
    { enableSort: false, width: '180px', enableFilter: true },
    { enableSort: false, width: '170px', enableFilter: true },
    { enableSort: false, width: '270px', enableFilter: true },
    { enableSort: false, width: '150px', enableFilter: true },
    { enableSort: false, width: '300px', enableFilter: true },
    { enableSort: false, width: '200px', enableFilter: true }
  ]
  const handleDownload = () => exportToExcel(excelData, 'Reclamos')

  const handleKeyPressDownload = event => {
    if (event.charCode === 13 || event.charCode === 32) {
      event.preventDefault()
      handleDownload()
    }
  }

  const getProcessClaimUrl = ({ mechanism, originId, processClaim }) => {
    if (!mechanism?.type || !mechanism?.url) return

    const urlByType = {
      CA: mechanism?.url?.replace('{mechanismCode}', processClaim),
      GC: mechanism?.url?.replace('{id}', originId),
      Indefinido: null,
      LIC: mechanism?.url?.replace('{mechanismCode}', processClaim),
      OC: mechanism?.url?.replace('{mechanismCode}', processClaim),
      TD: mechanism?.url?.replace('{mechanismCode}', processClaim)
    }

    const url = urlByType[mechanism?.type] || null

    return url
  }

  const handleClickProcessClaim =
    ({ mechanism, originId, processClaim }) =>
    () => {
      const url = getProcessClaimUrl({ mechanism, originId, processClaim })

      if (!url) return

      window.open(url, 'Ficha_Compra')
    }

  const handleKeyPress =
    ({ mechanism, originId, processClaim }) =>
    event => {
      if (event.charCode === 13 || event.charCode === 32) {
        event.preventDefault()

        const url = getProcessClaimUrl({ mechanism, originId, processClaim })

        if (!url) return

        window.open(url, 'Ficha_Compra')
      }
    }

  useEffect(() => {
    if (gridData.length > 0) {
      setdataInfo(gridData)
    }
  }, [gridData])

  useEffectOnce(() => {
    dispatch(onGetClaimInformationTableThunk(dni, claimValue))
  }, [dispatch, claimValue, dni])

  return (
    <Root>
      <Typography
        color="black1"
        variant="h4"
        fontWeight="bold"
        fontSize="18px"
        lineHeight="22px"
        margin="0 0 20px 0"
        tabIndex="0"
      >
        Detalle de reclamos recibidos
      </Typography>
      {loading && !error && (
        <Row>
          <Skeleton height="400px" variant="rectangular" width="100%" />
        </Row>
      )}

      {!loading && !error && !loadingSpinner && hasData && (
        <Card display="flex" color="blank" enableBorder>
          <TableContainer>
            <DataTable
              accessibility={{ label: 'Tabla detalles de reclamos recibidos' }}
              header={
                <GridItem xs={12} md={6} lg={6}>
                  <Select
                    label="Tipo de reclamo"
                    options={CLAIMS_OPTIONS}
                    value={claimValue}
                    onChange={handleChangeOption}
                  />
                </GridItem>
              }
              enablePagination
              showPagination
              enableResults
              initialRows={totalRows}
              loading={loading}
              headers={headerTable}
              headersCellsProps={claimTableProp}
              records={dataInfo}
              onSort={() => undefined}
              recordsCellsProps={[
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'center', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' },
                { alignment: 'left', verticalAlign: 'middle' }
              ]}
              recordsDecorators={[
                ({ claimId }) => claimId,
                ({ mechanism, originId, processClaim }) => (
                  <TableLink
                    tabIndex="0"
                    textDecoration="underline"
                    color="primary"
                    display="inline"
                    onClick={handleClickProcessClaim({ mechanism, originId, processClaim })}
                    onKeyPress={handleKeyPress({ mechanism, originId, processClaim })}
                  >
                    {processClaim}
                  </TableLink>
                ),
                ({ typeOfClaim }) => typeOfClaim,
                ({ entryDate }) => entryDate,
                ({ complainant }) => complainant,
                ({ status }) => status
              ]}
            />
          </TableContainer>
          <Link
            tabIndex="0"
            color="primary"
            textDecoration="underline"
            onClick={handleDownload}
            onKeyPress={handleKeyPressDownload}
          >
            <ArrowDownIcon /> Descargar listado en excel
          </Link>
        </Card>
      )}

      {!loading && !error && !loadingSpinner && !hasData && (
        <Card margin="24px 0 30px 0" display="flex" enableBorder color="blank" textAlign="center">
          <NotFoundImg
            alt="No se encontraron resultados"
            margin="48px 0 0 0"
            src={`${PUBLIC_URL}/assets/images/notFound.svg`}
          />
          <Typography
            color="black1"
            variant="h4"
            fontWeight="bold"
            fontSize="18px"
            lineHeight="22px"
            margin="10px"
            tabIndex="0"
          >
            No existe información
          </Typography>
          <Typography
            color="black3"
            variant="body2"
            fontWeight="regular"
            fontSize="14px"
            lineHeight="18px"
            margin="5px 0 52px 0"
            tabIndex="0"
          >
            El organismo comprador no ha recibido
            <br />
            reclamos durante los últimos 12 meses.
          </Typography>
        </Card>
      )}

      {error && (
        <Card
          display="flex"
          enableBorder
          color="blank"
          margin="43px 0 44px 0"
          alignItems="center"
          height={isTiny ? '139px' : '243px'}
        >
          <CardItem direction="row" padding="25px 16px">
            <WarningImg alt="No se encontraron resultados" src={`${PUBLIC_URL}/assets/images/warning.svg`} />
            <Typography
              color="black1"
              variant="body1"
              fontWeight="regular"
              fontSize="14px"
              lineHeight="18px"
              margin="0 0 0 33px"
              tabIndex="0"
            >
              No ha sido posible obtener información de la institución.
              <br />
              Por favor, te pedimos que lo vuelvas a intentar dentro de unos minutos.
            </Typography>
          </CardItem>
        </Card>
      )}

      {!loading && loadingSpinner && (
        <Card margin="0 0 50px 0" display="flex" enableBorder color="blank">
          <CardItem direction="row" sx={{ margin: '97px auto' }}>
            <Spinner />
          </CardItem>
        </Card>
      )}

      {!loading && (
        <Row flexWrap="nowrap" gap="26px" marginTop="32px" justifyContent="left">
          <ColumnChart lg={6} sm={6} xs={12} margin={isUpToMedium ? '0 13px 0 0' : '0 0 28px 0'}>
            <RowChart padding="0px 16px 14px 16px">
              <Row>
                <Typography
                  color="black1"
                  fontSize="18px"
                  fontWeight="bold"
                  variant="h4"
                  lineHeight="22px"
                  tabIndex="0"
                >
                  Información sobre reclamos
                </Typography>
              </Row>
              <Row>
                <Typography
                  color="black1"
                  variant="body1"
                  fontWeight="regular"
                  fontSize="16px"
                  lineHeight="21px"
                  tabIndex="0"
                  width={isTiny ? '305px' : '467px'}
                >
                  Si deseas conocer información sobre los reclamos recibidos por esta institución, puedes hacerlo a
                  través del Buscador de Reclamos de Mercado Público.
                </Typography>
              </Row>
              <Row justifyContent="end" alignItems="end">
                <Link
                  target="_blank"
                  href={REACT_APP_SEARCH_CLAIM_INFO}
                  color="primary"
                  display="inline"
                  rel="noreferrer"
                  fontWeight="bold"
                >
                  Ir al Buscador de Reclamos <ArrowForwardIcon />
                </Link>
              </Row>
            </RowChart>
          </ColumnChart>
        </Row>
      )}
    </Root>
  )
}
export default ClaimsReceivedChartSection
